import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { PageTitleService } from '../../../core/services/page-title.service';

interface TransferBook { bookSrNo: number; bookName: string; }

@Component({
  selector: 'app-inter-center-transfer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './inter-center-transfer.component.html',
  styleUrl: './inter-center-transfer.component.scss',
})
export class InterCenterTransferComponent implements OnInit {

  @ViewChild('issueSrInput')   issueSrInput!:   ElementRef<HTMLInputElement>;
  @ViewChild('receiveSrInput') receiveSrInput!: ElementRef<HTMLInputElement>;

  isAdmin   = false;
  isManager = false;

  // Centers
  allCenters: any[] = [];
  primaryCenter: any = null;
  secondaryCenterId = '';

  get secondaryCenter(): any {
    return this.allCenters.find(c => c._id === this.secondaryCenterId) || null;
  }
  get availableSecondaryCenters(): any[] {
    return this.allCenters.filter(c => c._id !== this.primaryCenter?._id);
  }

  // Date
  transferDate = '';

  // Issue (outgoing from primary)
  issueSrNo = '';
  issueBooks: TransferBook[] = [];
  issueProcessing = false;   // true while an API call is in flight
  private issueQueue: string[] = [];
  issueError = '';

  // Receive (incoming to primary)
  receiveSrNo = '';
  receiveBooks: TransferBook[] = [];
  receiveProcessing = false;
  private receiveQueue: string[] = [];
  receiveError = '';

  // Execute
  isExecuting = false;
  executeError = '';
  successResult: any = null;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private centerSvc: CenterService,
    private pageTitle: PageTitleService,
  ) {}

  ngOnInit(): void {
    this.pageTitle.set('Inter-Center Transfer', 'Transfer books between library centers');
    const user = this.auth.getCurrentUser();
    this.isAdmin   = user?.role?.toLowerCase() === 'admin';
    this.isManager = user?.role?.toLowerCase() === 'manager';
    this.transferDate = new Date().toISOString().slice(0, 10);

    this.api.getCenters().subscribe({
      next: (centers) => {
        this.allCenters = centers || [];
        if (this.isManager) {
          this.primaryCenter = this.centerSvc.selectedCenter;
        }
      },
      error: () => {},
    });
  }

  setPrimaryCenter(centerId: string): void {
    this.primaryCenter = this.allCenters.find(c => c._id === centerId) || null;
    if (this.secondaryCenterId === centerId) this.secondaryCenterId = '';
    this.issueBooks    = [];
    this.receiveBooks  = [];
    this.issueQueue    = [];
    this.receiveQueue  = [];
    this.issueError    = '';
    this.receiveError  = '';
  }

  // ── Issue ─────────────────────────────────────────────────────────────────

  addIssueBook(): void {
    const srNo = this.issueSrNo.trim();
    if (!srNo) return;
    if (!this.primaryCenter) { this.issueError = 'Please select the primary center first.'; return; }

    // Clear the input immediately so the scanner can fire the next code right away
    this.issueSrNo = '';
    this.issueError = '';
    this.issueQueue.push(srNo);
    this._drainIssueQueue();
  }

  private _drainIssueQueue(): void {
    if (this.issueProcessing || this.issueQueue.length === 0) return;
    const srNo = this.issueQueue.shift()!;

    if (this.issueBooks.find(b => Number(b.bookSrNo) === Number(srNo))) {
      this.issueError = `Copy ${srNo} is already in the issue list.`;
      this._drainIssueQueue();   // skip and process next
      return;
    }

    this.issueProcessing = true;
    this.api.ictValidateForIssue(srNo, this.primaryCenter._id).subscribe({
      next: (res: any) => {
        this.issueBooks = [...this.issueBooks, { bookSrNo: res.bookSrNo, bookName: res.bookName }];
        this.issueProcessing = false;
        this._drainIssueQueue();
      },
      error: (err: any) => {
        this.issueError = `${srNo}: ${err?.message || 'Not found or not at primary center.'}`;
        this.issueProcessing = false;
        this._drainIssueQueue();
      },
    });
  }

  removeIssueBook(srNo: number): void {
    this.issueBooks = this.issueBooks.filter(b => b.bookSrNo !== srNo);
  }

  // ── Receive ───────────────────────────────────────────────────────────────

  addReceiveBook(): void {
    const srNo = this.receiveSrNo.trim();
    if (!srNo) return;

    // Clear immediately so the scanner can fire the next code right away
    this.receiveSrNo = '';
    this.receiveError = '';
    this.receiveQueue.push(srNo);
    this._drainReceiveQueue();
  }

  private _drainReceiveQueue(): void {
    if (this.receiveProcessing || this.receiveQueue.length === 0) return;
    const srNo = this.receiveQueue.shift()!;

    if (this.receiveBooks.find(b => Number(b.bookSrNo) === Number(srNo))) {
      this.receiveError = `Copy ${srNo} is already in the receive list.`;
      this._drainReceiveQueue();
      return;
    }

    this.receiveProcessing = true;
    this.api.ictLookupCopy(srNo).subscribe({
      next: (res: any) => {
        this.receiveBooks = [...this.receiveBooks, { bookSrNo: res.bookSrNo, bookName: res.bookName }];
        this.receiveProcessing = false;
        this._drainReceiveQueue();
      },
      error: (err: any) => {
        this.receiveError = `${srNo}: ${err?.message || 'Copy not found.'}`;
        this.receiveProcessing = false;
        this._drainReceiveQueue();
      },
    });
  }

  removeReceiveBook(srNo: number): void {
    this.receiveBooks = this.receiveBooks.filter(b => b.bookSrNo !== srNo);
  }

  // ── Execute ───────────────────────────────────────────────────────────────

  get canExecute(): boolean {
    return !!this.primaryCenter && !!this.secondaryCenterId &&
           (this.issueBooks.length > 0 || this.receiveBooks.length > 0) &&
           !this.issueProcessing && !this.receiveProcessing;
  }

  execute(): void {
    if (!this.canExecute || this.isExecuting) return;
    this.isExecuting = true;
    this.executeError = '';
    const userId = this.auth.getCurrentUser()?.userID;
    this.api.ictExecute({
      primaryCenterId:   this.primaryCenter._id,
      secondaryCenterId: this.secondaryCenterId,
      issuedSrNos:       this.issueBooks,
      receivedSrNos:     this.receiveBooks,
      userId,
    }).subscribe({
      next: (result: any) => {
        this.successResult = result;
        this.isExecuting = false;
      },
      error: (err: any) => {
        this.executeError = err?.error?.message || err?.message || 'Transfer failed. Please try again.';
        this.isExecuting = false;
      },
    });
  }

  startNew(): void {
    this.successResult    = null;
    this.issueBooks       = [];
    this.receiveBooks     = [];
    this.issueQueue       = [];
    this.receiveQueue     = [];
    this.secondaryCenterId = '';
    this.issueError       = '';
    this.receiveError     = '';
    this.executeError     = '';
  }
}
